import React, { useState, useMemo } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  FormConfig,
  FormField,
  FormService,
} from "@shared/services/FormServices";
import { useNotificationChoices } from "@features/ux/hooks";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";
import { usePostDirectMessage } from "../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { logEvent } from "@shared/analytics/analytics";
import { Button } from "@shared/ui/button";
import { Loader2 } from "lucide-react";

const IndividualMessage: React.FC = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const { mutate: sendDirectMessage, isPending } = usePostDirectMessage();

  const { data: channelTypes } = useNotificationChoices("channel_type");
  const channelTypeChoice = mapToLabelValue(channelTypes);

  const [formState, setFormState] = useState({
    channel: "",
    recipientEmail: "",
    recipientMobile: "",
    cc: "", // Keep separate
    bcc: "", // Add BCC field
    subject: "",
    message: "",
    scheduleForLater: false,
    scheduledDate: "",
    scheduledTime: "",
  });

  const fields: FormField[] = useMemo(
    () => [
      {
        name: "channel",
        type: "select",
        label: "Channel",
        required: true,
        placeholder: "Choose Channel Type",
        helperText: "Choose how you want to deliver this message.",
        options: channelTypeChoice,
      },
      {
        name: "recipientEmail",
        type: "email",
        label: "Recipient Email",
        placeholder: "recipient@example.com",
        required: formState.channel === "EMAIL",
        showWhen: () => formState.channel === "EMAIL",
        helperText: "Enter the email address of the recipient.",
      },
      {
        name: "recipientMobile",
        type: "number",
        label: "Mobile Number",
        placeholder: "+1234567890",
        helperText: "Enter the mobile number of the recipient.",
        required: false,
        showWhen: () => formState.channel && formState.channel !== "EMAIL",
      },
      {
        name: "cc",
        type: "text",
        label: "CC (Carbon Copy)",
        placeholder: "Enter email addresses separated by commas",
        className: "col-span-1", // Changed from col-span-2
        required: false, // Changed from required
        showWhen: () => formState.channel === "EMAIL",
        helperText: "Add email addresses for CC (comma-separated)",
      },
      {
        name: "bcc",
        type: "text",
        label: "BCC (Blind Carbon Copy)",
        placeholder: "Enter email addresses separated by commas",
        className: "col-span-1", // Changed from col-span-2
        required: false,
        showWhen: () => formState.channel === "EMAIL",
        helperText: "Add email addresses for BCC (comma-separated)",
      },
      {
        name: "subject",
        type: "text",
        label: "Subject",
        placeholder: "Message subject",
        className: "col-span-2",
        required: formState.channel === "EMAIL",
        showWhen: () => formState.channel === "EMAIL",
      },
      {
        name: "message",
        type: "textarea",
        label: "Message",
        required: true,
        placeholder: "Type your message here...",
        rows: 6,
        className: "col-span-2",
      },
      {
        name: "scheduleForLater",
        type: "checkbox",
        label: "Schedule for later",
        className: "col-span-2",
      },
      {
        name: "scheduledDate",
        type: "datepicker",
        label: "Scheduled Date",
        placeholder: "Pick a date",
        required: formState.scheduleForLater === true,
        showWhen: () => formState.scheduleForLater === true,
        className: "col-span-1",
        customValidation: (value: any) => {
          if (!value) return true; // Allow empty if not required

          const selectedDate = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (selectedDate < today) {
            return "Please select a date from today onwards";
          }
          return true;
        },
      },
      {
        name: "scheduledTime",
        type: "time",
        label: "Scheduled Time",
        placeholder: "Select time",
        required: formState.scheduleForLater === true,
        showWhen: () => formState.scheduleForLater === true,
        className: "col-span-1",
      },
    ],
    [formState, channelTypeChoice]
  );

  const config: FormConfig = {
    fields,
    showResetButton: false, // Hide default buttons
    showSubmitButton: false, // Hide default buttons
    resetOnSubmit: false, // Changed to false since we handle reset manually
    layout: "grid",
    classes: {
      fieldContainer: "flex flex-col gap-1 mb-4",
      label: "text-sm text-gray-800 font-semibold",
      input: "rounded-md border px-3 py-2 border-gray-300 w-full",
      select: "rounded-md border px-3 py-2 border-gray-300",
      textarea: "rounded-md border px-3 py-2 border-gray-300",
      error: "text-sm text-red-500 mt-1",
      helperText: "text-xs text-gray-500 mt-1",
      buttonContainer: "hidden", // Hide the default button container
    },
  };

  const form = FormService.createForm(config);

  const handleSubmit = async (data: any) => {
    let scheduledDateTime = null;

    // Combine date and time if both are provided
    if (data.scheduleForLater && data.scheduledDate && data.scheduledTime) {
      scheduledDateTime = `${data.scheduledDate}T${data.scheduledTime}`;
    } else if (data.scheduleForLater && data.scheduledDate) {
      scheduledDateTime = data.scheduledDate;
    }

    const payload = {
      remote_utility_id: remoteUtilityId,
      channel_type: data.channel,
      to:
        data.channel === "EMAIL"
          ? [data.recipientEmail]
          : [data.recipientMobile],
      body: data.message,
      ...(scheduledDateTime && { scheduled_date: scheduledDateTime }),
    };

    // Add CC if provided
    if (data.cc) {
      const ccArray = data.cc
        .split(",")
        .map((email: string) => email.trim())
        .filter((email: string) => email.length > 0);
      payload["cc"] = ccArray;
    }

    // Add BCC if provided
    if (data.bcc) {
      const bccArray = data.bcc
        .split(",")
        .map((email: string) => email.trim())
        .filter((email: string) => email.length > 0);
      payload["bcc"] = bccArray;
    }

    if (data.subject) {
      payload["subject"] = data.subject;
    }

    sendDirectMessage(payload, {
      onSuccess: () => {
        handleReset();
      },
    });
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormState((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    logEvent(`Individual Messaging ${fieldName} Field Selected`, {
      field: fieldName,
      value: value,
    })
  };

  const handleReset = () => {
    const resetValues = {
      channel: "",
      recipientEmail: "",
      recipientMobile: "",
      cc: "",
      bcc: "", // Add BCC to reset
      subject: "",
      message: "",
      scheduleForLater: false,
      scheduledDate: "",
      scheduledTime: "",
    };
    setFormState(resetValues);
    form.reset(resetValues);
  };

  // Custom footer component similar to UtilityAddForm
  const renderCustomFooter = () => (
    <div className="flex justify-between gap-3 mt-6 pt-4 border-t">
      <Button
        type="button"
        variant="outline"
        onClick={handleReset}
        disabled={isPending}
      >
        Reset Form
      </Button>
      <Button
        type="submit"
        disabled={isPending}
        onClick={() => {
          form.handleSubmit(handleSubmit);
        }}
      >
        {isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : formState.scheduleForLater === true ? (
          "Schedule Message"
        ) : (
          "Send"
        )}
      </Button>
    </div>
  );

  return (
    <div className="bg-white">
      <div className="mb-8">
        <p className="card-subheading-text">
          Send a message to an individual recipient through your preferred
          channel.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <DynamicForm
          fields={fields}
          form={form}
          onSubmit={handleSubmit}
          onReset={handleReset}
          onFieldChange={(fieldName, value) => handleFieldChange(fieldName, value)}
          config={config}
          gridColumns={2}
          responsiveColumns={{ sm: 1, md: 2 }}
          className="messaging-form"
          loading={isPending}
          renderCustomFooter={renderCustomFooter}
        />
      </div>
    </div>
  );
};

export default IndividualMessage;
